import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilSquareIcon, 
  CheckIcon, 
  XMarkIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAppData } from '../../contexts/AppDataContext';

const ProductCategories = forwardRef((props, ref) => {
  const { 
    products, 
    productCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    updateProduct
  } = useAppData();
  
  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [categories, setCategories] = useState([]);
  const inputRef = useRef(null);

  // Extraire les catégories depuis le contexte
  useEffect(() => {
    if (productCategories && productCategories.length > 0) {
      setCategories(productCategories);
    } else {
      // Fallback: extraire des produits si productCategories n'est pas disponible
      const uniqueCategories = [...new Set(products.map(product => product.category))].filter(Boolean);
      setCategories(uniqueCategories);
    }
  }, [productCategories, products]);

  // Exposer la fonction d'ajout de catégorie via ref
  useEffect(() => {
    if (ref) {
      ref.current = () => {
        if (inputRef.current) {
          inputRef.current.focus();
          
          // Vérifier si newCategory est rempli
          if (newCategory.trim()) {
            handleAddCategory();
          } else {
            toast.error('Veuillez saisir un nom de catégorie');
          }
        }
      };
    }
  }, [ref, newCategory]);

  // Compter les produits dans chaque catégorie
  const getCategoryCount = (categoryName) => {
    return products.filter(product => product.category === categoryName).length;
  };

  // Ajouter une nouvelle catégorie
  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      toast.error('Veuillez saisir un nom de catégorie');
      return;
    }

    if (categories.includes(newCategory.trim())) {
      toast.error('Cette catégorie existe déjà');
      return;
    }

    // Utiliser la fonction du contexte pour ajouter une catégorie
    const result = addCategory(newCategory.trim());
    if (result) {
      setNewCategory('');
      toast.success('Catégorie ajoutée avec succès');
    }
  };

  // Commencer à éditer une catégorie
  const startEditing = (category) => {
    setEditingCategory(category);
    setEditValue(category);
  };

  // Annuler l'édition
  const cancelEditing = () => {
    setEditingCategory(null);
    setEditValue('');
  };

  // Sauvegarder les modifications
  const saveEdit = (oldCategory) => {
    if (!editValue.trim()) {
      toast.error('Le nom de la catégorie ne peut pas être vide');
      return;
    }

    if (categories.includes(editValue.trim()) && editValue.trim() !== oldCategory) {
      toast.error('Cette catégorie existe déjà');
      return;
    }

    // Utiliser la fonction du contexte pour mettre à jour la catégorie
    const result = updateCategory(oldCategory, editValue.trim());
    if (result) {
      setEditingCategory(null);
      toast.success('Catégorie mise à jour avec succès');
    } else {
      toast.error('Erreur lors de la mise à jour de la catégorie');
    }
  };

  // Supprimer une catégorie
  const handleDeleteCategory = (category) => {
    const count = getCategoryCount(category);
    
    if (count > 0) {
      toast.error(`Impossible de supprimer: ${count} produit(s) dans cette catégorie`);
      return;
    }

    // Utiliser la fonction du contexte pour supprimer la catégorie
    const result = deleteCategory(category);
    if (result) {
      toast.success('Catégorie supprimée avec succès');
    } else {
      toast.error('Erreur lors de la suppression de la catégorie');
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <TagIcon className="h-6 w-6 mr-2 text-blue-400" />
          Catégories de produits
        </h2>
      </div>
      
      <div className="flex items-center space-x-2">
        <input
          ref={inputRef}
          type="text"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="Nouvelle catégorie..."
          className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddCategory();
            }
          }}
        />
        <button
          onClick={handleAddCategory}
          className="p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl flex items-center transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>
      
      <div className="divide-y divide-white/10">
        {categories.length > 0 ? (
          categories.map((category) => (
            <div 
              key={category} 
              className="py-3 flex items-center justify-between"
            >
              {editingCategory === category ? (
                <div className="flex-1 flex items-center space-x-2">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 p-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveEdit(category);
                      } else if (e.key === 'Escape') {
                        cancelEditing();
                      }
                    }}
                  />
                  <button 
                    onClick={() => saveEdit(category)}
                    className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={cancelEditing}
                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center">
                    <span className="text-white font-medium">{category}</span>
                    <span className="ml-3 px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                      {getCategoryCount(category)} produit(s)
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => startEditing(category)}
                      className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30"
                      title="Modifier"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteCategory(category)}
                      className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                      title="Supprimer"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <div className="py-4 text-center text-white/60">
            Aucune catégorie disponible
          </div>
        )}
      </div>
    </div>
  );
});

export default ProductCategories; 